import React, { useEffect } from 'react';
import {
	graphql,
	useFragment,
	useRelayEnvironment,
	commitLocalUpdate,
	useLazyLoadQuery,
} from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { ThrowOnQueryError } from '@jira/servicedesk__customer-service-common/src/ThrowOnQueryError.tsx';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import Presence from '@atlaskit/avatar/presence';
import RelayDataID from '@atlassian/relay-data-id';
import Placeholder from '@atlassian/jira-placeholder';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

import type { LiveConversationsActivityStatusIndicator_query$key } from './__generated__/LiveConversationsActivityStatusIndicator_query.graphql';
import type { LiveConversationsActivityStatusIndicatorQuery } from './__generated__/LiveConversationsActivityStatusIndicatorQuery.graphql';
import { AsyncTwilioActivityStatusIndicator } from './AsyncTwilioActivityStatusIndicator.tsx';
import { ActivityStatusIndicatorPresence } from './ActivityStatusIndicatorPresence.tsx';

const styles = cssMap({
	presenceWrapper: {
		position: 'absolute',
		height: '12px',
		width: '12px',
		bottom: token('space.025'),
		right: token('space.025'),
	},
});

const Inner = ({
	queryFragment,
}: {
	queryFragment: LiveConversationsActivityStatusIndicator_query$key;
}) => {
	const environment = useRelayEnvironment();

	const queryData = useFragment(
		graphql`
			fragment LiveConversationsActivityStatusIndicator_query on Query {
				csm_liveChatAgent(accountId: $atlassianAccountId, cloudId: $cloudId)
					@optIn(to: "CustomerServiceOmniChannelLiveChat") {
					__typename
					... on CustomerServiceLiveChatAgent {
						id
						liveChatActivity
						liveChatActivitiesBySpace {
							edges {
								__typename
							}
						}
					}
					... on QueryError {
						...ThrowOnQueryError @dangerously_unaliased_fixme
					}
					...ActivityStatusIndicatorPresence_agent @dangerously_unaliased_fixme
				}
			}
		`,
		queryFragment,
	);

	const agentId =
		queryData?.csm_liveChatAgent?.__typename === 'CustomerServiceLiveChatAgent'
			? queryData.csm_liveChatAgent.id
			: null;

	// Listen for the twilio flex SDK being instantiated from elsewhere
	useEffect(() => {
		if (typeof globalThis.BroadcastChannel === 'undefined' || !agentId) return;

		const channel = new BroadcastChannel('jira-csm-live-conversations');
		channel.onmessage = (event: MessageEvent<string>) => {
			if (event.data === 'TWILIO_CLIENT_INITIALIZED') {
				channel.close();
				commitLocalUpdate(environment, (store) => {
					const storeKey = RelayDataID({ id: agentId }, 'CustomerServiceLiveChatAgent');
					const record = storeKey ? store.get(storeKey) : null;

					if (record) {
						record.setValue('available', 'liveChatActivity');
					}
				});
			}
		};

		return () => {
			channel.close();
		};
	}, [agentId, environment]);

	if (queryData?.csm_liveChatAgent?.__typename === 'QueryError') {
		return (
			<ThrowOnQueryError
				operationName="LiveConversationsActivityStatusIndicator"
				errorRef={queryData.csm_liveChatAgent}
			/>
		);
	}

	if (queryData?.csm_liveChatAgent?.__typename !== 'CustomerServiceLiveChatAgent') {
		return null;
	}

	if (!queryData.csm_liveChatAgent.liveChatActivitiesBySpace?.edges?.length) {
		return null;
	}

	return (
		<Box xcss={styles.presenceWrapper}>
			{queryData.csm_liveChatAgent.liveChatActivity === 'available' ? (
				// This needs to be async to keep heavy twilio flex sdk off the main bundle
				// plus we only want to instantiate the twilio flex SDK when the user is actually online to save costs
				<Placeholder
					name="twilio-activity-status-inner"
					fallback={
						<ActivityStatusIndicatorPresence
							agentFragment={queryData.csm_liveChatAgent}
							workerActivity={queryData.csm_liveChatAgent.liveChatActivity}
						/>
					}
				>
					<AsyncTwilioActivityStatusIndicator agentFragment={queryData.csm_liveChatAgent} />
				</Placeholder>
			) : (
				<Presence presence="offline" />
			)}
		</Box>
	);
};

const InnerWithLoader = () => {
	const accountId = useAccountId();
	const cloudId = useCloudId();

	const data = useLazyLoadQuery<LiveConversationsActivityStatusIndicatorQuery>(
		graphql`
			query LiveConversationsActivityStatusIndicatorQuery($atlassianAccountId: ID!, $cloudId: ID!) {
				...LiveConversationsActivityStatusIndicator_query
			}
		`,
		{ atlassianAccountId: accountId ?? '', cloudId },
	);

	return <Inner queryFragment={data} />;
};

export const LiveConversationsActivityStatusIndicator = ({
	queryFragment,
}: {
	queryFragment?: LiveConversationsActivityStatusIndicator_query$key | null;
}): React.JSX.Element => (
	<JSErrorBoundary
		id="LiveConversationsActivityStatusIndicator"
		packageName="jiraCustomerServiceLiveConversationsActivityStatus"
		teamName="boysenberry"
		fallback="unmount"
		sendToPrivacyUnsafeSplunk
	>
		<Placeholder
			name="twilio-activity-status"
			fallback={
				<Box xcss={styles.presenceWrapper}>
					<Presence presence="offline" />
				</Box>
			}
		>
			{queryFragment ? <Inner queryFragment={queryFragment} /> : <InnerWithLoader />}
		</Placeholder>
	</JSErrorBoundary>
);
